/**
 * Global loading-bar store — single source of truth for the indeterminate
 * progress indicator displayed by `<GlobalLoadingBar>` in AppLayout.
 *
 * Why this exists:
 *   The pre-Sprint-7 codebase rendered a separate `<TopLoadingBar>` per
 *   page, scoped to a Card or absolutely positioned. In the worst case
 *   (DashboardPage) the wrapper used `position: fixed; left: 0; right: 0`
 *   which extended the blue bar across the sidebar — recruiters reported
 *   it as "the loader starts from the sidebar instead of the content".
 *   Sprint 7+ (21/05/26) finished the migration: every hook now publishes
 *   to this store and the only visible bar lives inside `<main>` in
 *   AppLayout, sticky under the header. The sidebar is never touched.
 *
 * API:
 *   - `useLoadingStore.getState().push(key)` / `pop(key)` — for callers
 *     that wrap arbitrary async work imperatively.
 *   - `useGlobalLoading(active, key?)` — declarative hook used by the
 *     data hooks (`useDashboardData`, `useJobPositions`,
 *     `useApplicationsPageData`, `usePostings`,
 *     `useApplicationDetail`).
 *
 * Concurrency:
 *   Counts are per-key, so concurrent fetches don't fight each other:
 *     A `push('positions')`      → bar shows
 *     B `push('billing')`        → bar still shows (count=2)
 *     A finishes  `pop`          → count=1, bar still shows
 *     B finishes  `pop`          → count=0, bar fades out (200ms)
 */

import { useEffect } from 'react';
import { create } from 'zustand';

interface LoadingState {
  /** Per-key reference count. 0 → bar hidden. */
  counts: Record<string, number>;
  push: (key: string) => void;
  pop: (key: string) => void;
  /** True when at least one key has count > 0. */
  isActive: () => boolean;
}

export const useLoadingStore = create<LoadingState>((set, get) => ({
  counts: {},
  push: (key) =>
    set((state) => ({
      counts: { ...state.counts, [key]: (state.counts[key] ?? 0) + 1 },
    })),
  pop: (key) =>
    set((state) => {
      const next = (state.counts[key] ?? 0) - 1;
      const counts = { ...state.counts };
      if (next <= 0) delete counts[key];
      else counts[key] = next;
      return { counts };
    }),
  isActive: () => Object.values(get().counts).some((c) => c > 0),
}));

/**
 * Selector hook — re-renders the consumer only when active flips.
 */
export function useGlobalLoadingActive(): boolean {
  return useLoadingStore((state) => Object.values(state.counts).some((c) => c > 0));
}

/**
 * Declarative hook: while `active` is true, the global bar is shown.
 * Idempotent — multiple components can use the same key.
 */
export function useGlobalLoading(active: boolean, key = 'global'): void {
  const push = useLoadingStore((s) => s.push);
  const pop = useLoadingStore((s) => s.pop);
  useEffect(() => {
    if (!active) return;
    push(key);
    return () => pop(key);
  }, [active, key, push, pop]);
}
